/**
 * Semantic search over the knowledge base.
 *
 * Takes a natural-language query, embeds it via the Voyage edge function,
 * and runs pgvector cosine similarity against the knowledge_chunks table
 * (filtered by tier visibility inside the `search_knowledge()` PG function).
 *
 * Used by:
 *   - Brain's `search_knowledge` tool (explicit lookup during chat)
 *   - Scratch respond, session planner, curriculum generator (auto-injection)
 *
 * Retrieval returns compact `KnowledgeHit` rows — enough to cite and quote,
 * not so much that prompts bloat. The caller decides how to format hits
 * for the prompt (we don't prescribe a template here).
 */
import { supabase } from './supabase';
import { debug, formatError } from '@4cm/shared';

const log = debug('knowledge');

export interface KnowledgeHit {
  chunkId: string;
  sourceId: string;
  sourceSlug: string;
  sourceTitle: string;
  sectionPath: string[];
  content: string;
  score: number;
  /** Pre-formatted citation string: "The 5Cs · Commitment → Definition". */
  cite: string;
}

export interface KnowledgeSearchOpts {
  coachId: string;
  matchCount?: number;
  /** Cosine similarity floor. search_knowledge() has its own default (0.4);
   *  pass a value here to override. Bumps above 0.5 give fewer/tighter hits. */
  scoreFloor?: number;
  onStatus?: (msg: string) => void;
}

function formatCite(title: string, path: string[]): string {
  const tail = path.filter((p) => !!p).join(' → ');
  return tail ? `${title} · ${tail}` : title;
}

/**
 * Search the knowledge base for relevant chunks. Returns empty array on
 * any failure — callers fall back to pre-knowledge behaviour rather than
 * blocking on retrieval.
 */
export async function searchKnowledge(
  query: string,
  opts: KnowledgeSearchOpts,
): Promise<KnowledgeHit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const onStatus = opts.onStatus ?? (() => {});
  const matchCount = opts.matchCount ?? 5;
  const scoreFloor = opts.scoreFloor ?? 0.4;

  onStatus('Embedding query…');
  let embedData: any = null;
  let embedErr: any = null;
  try {
    const res = await supabase.functions.invoke('embed', {
      body: { text: trimmed, input_type: 'query' },
    });
    embedData = res.data;
    embedErr = res.error;
  } catch (err) {
    // CORS preflight / network failure / edge function not deployed
    // yet — never propagate to the caller; retrieval is advisory, not
    // load-bearing.
    embedErr = err;
  }
  if (embedErr || !embedData?.embeddings?.[0]) {
    log.warn('embed failed', { message: formatError(embedErr), query: trimmed.slice(0, 80) });
    return [];
  }
  const queryEmbedding = embedData.embeddings[0] as number[];

  onStatus('Searching knowledge base…');
  const { data, error } = await supabase.rpc('search_knowledge', {
    query_embedding: queryEmbedding,
    p_coach_id: opts.coachId,
    match_count: matchCount,
    score_floor: scoreFloor,
  });
  if (error) {
    log.warn('search_knowledge rpc failed', { message: error.message });
    return [];
  }
  if (!Array.isArray(data)) return [];

  const hits: KnowledgeHit[] = data.map((r: any) => ({
    chunkId: r.chunk_id,
    sourceId: r.source_id,
    sourceSlug: r.source_slug,
    sourceTitle: r.source_title,
    sectionPath: Array.isArray(r.section_path) ? r.section_path : [],
    content: r.content,
    score: typeof r.score === 'number' ? r.score : 0,
    cite: formatCite(r.source_title ?? r.source_slug ?? '?', Array.isArray(r.section_path) ? r.section_path : []),
  }));

  log.info('search', {
    query: trimmed.slice(0, 80),
    hits: hits.length,
    topScore: hits[0]?.score,
    topCite: hits[0]?.cite,
  });

  return hits;
}

/**
 * Format a set of hits as a prompt-ready `<relevant_frameworks>` XML-ish
 * block. Caller picks the block tag; the shape inside is consistent so
 * system prompts can rely on it.
 */
export function formatHitsForPrompt(hits: KnowledgeHit[], opts?: { blockTag?: string }): string {
  if (hits.length === 0) return '';
  const tag = opts?.blockTag ?? 'relevant_frameworks';
  const lines: string[] = [];
  lines.push(`<${tag}>`);
  for (const h of hits) {
    lines.push(`[${h.cite}]`);
    lines.push(h.content);
    lines.push('');
  }
  lines.push(`</${tag}>`);
  return lines.join('\n');
}
